import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronDown, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { useAgent } from "@/contexts/AgentContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";

interface LeadRow {
  id: string;
  lead_type_name: string;
  lead_type_id: string;
  state: string;
  status: string;
  agent_status: string | null;
  created_at: number;
  lead_profile: Record<string, unknown> | null;
}

// Lovable deployed agent_status with default 'pending' instead of 'new'.
// Normalize for pipeline display.
function normalizeAgentStatus(raw: string | null): string {
  if (!raw || raw === "pending") return "new";
  return raw;
}

// Pipeline tabs — shown in order of the sales process
const PIPELINE_TABS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "no_answer", label: "No Answer" },
  { key: "qualified", label: "Qualified" },
  { key: "proposal_sent", label: "Proposal Sent" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
  { key: "not_qualified", label: "Not Qualified" },
] as const;

const pipelineStyle: Record<string, { label: string; className: string }> = {
  new: { label: "New", className: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800" },
  contacted: { label: "Contacted", className: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800" },
  no_answer: { label: "No Answer", className: "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-800" },
  qualified: { label: "Qualified", className: "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/30 dark:text-purple-300 dark:border-purple-800" },
  not_qualified: { label: "Not Qualified", className: "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-700" },
  proposal_sent: { label: "Proposal Sent", className: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-300 dark:border-indigo-800" },
  won: { label: "Won", className: "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-800" },
  lost: { label: "Lost", className: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-800" },
};

const defaultPipelineStyle = { label: "New", className: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800" };

function getLeadName(profile: Record<string, unknown> | null): string {
  if (!profile) return "Unknown";
  const first = String(profile.first_name || "").trim();
  const last = String(profile.last_name || "").trim();
  if (first || last) return `${first} ${last}`.trim();
  return "Unknown";
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatTimestamp(ts: number): string {
  const date = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffHours < 1) return "Just now";
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffHours < 48) return "Yesterday";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const Leads = () => {
  const { state: agentState } = useAgent();
  const isMobile = useIsMobile();
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [pipelineFilter, setPipelineFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");

  useEffect(() => {
    const fetchLeads = async () => {
      const agentId = agentState.profile.id;
      if (!agentId) return;

      const { data, error } = await supabase
        .from("leads")
        .select("id, lead_type_name, lead_type_id, state, status, agent_status, created_at, lead_profile")
        .eq("routed_to_agent_id", agentId)
        .in("status", ["ROUTED", "ARCHIVED", "CREDITED"])
        .order("created_at", { ascending: false });

      if (error) {
        console.error("[Leads] Failed to fetch leads:", error);
      }
      setLeads((data as LeadRow[] | null) ?? []);
      setLoading(false);
    };

    fetchLeads();
  }, [agentState.profile.id]);

  const filteredLeads = leads.filter((lead) => {
    const name = getLeadName(lead.lead_profile);
    const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase());
    const agentStatus = normalizeAgentStatus(lead.agent_status);
    const matchesPipeline = pipelineFilter === "all" || agentStatus === pipelineFilter;
    const matchesType = typeFilter === "all" || lead.lead_type_name === typeFilter;
    return matchesSearch && matchesPipeline && matchesType;
  });

  const leadTypes = [...new Set(leads.map((l) => l.lead_type_name))];

  // Pipeline counts for tabs
  const pipelineCounts = leads.reduce<Record<string, number>>((acc, lead) => {
    const status = normalizeAgentStatus(lead.agent_status);
    acc[status] = (acc[status] ?? 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-muted-foreground">Loading leads...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">My Leads</h1>
            <p className="text-sm text-muted-foreground">Manage and track your assigned leads</p>
          </div>
          <span className="text-sm text-muted-foreground">
            {filteredLeads.length} of {leads.length} leads
          </span>
        </div>

        {leads.length === 0 ? (
          <div className="rounded-2xl border bg-card p-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-accent" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">No leads yet</h2>
            <p className="text-muted-foreground">
              Leads will appear here as they are routed to you based on your contract and preferences.
            </p>
          </div>
        ) : (
          <>
            {/* Pipeline Tabs */}
            <div className="flex gap-1 overflow-x-auto pb-1">
              {PIPELINE_TABS.map((tab) => {
                const count = tab.key === "all" ? leads.length : (pipelineCounts[tab.key] ?? 0);
                const isActive = pipelineFilter === tab.key;
                if (tab.key !== "all" && count === 0) return null;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setPipelineFilter(tab.key)}
                    className={`
                      flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors
                      ${isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }
                    `}
                  >
                    {tab.label}
                    <span className={`
                      text-xs px-1.5 py-0.5 rounded-full
                      ${isActive ? "bg-primary-foreground/20" : "bg-muted"}
                    `}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative flex-1 min-w-0 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="relative">
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="appearance-none bg-card border border-border rounded-lg px-4 py-2 pr-10 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="all">All Types</option>
                  {leadTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              </div>
            </div>

            {/* Leads Table / Cards */}
            <div className="bg-card rounded-2xl border border-border overflow-hidden">
              {isMobile ? (
                /* Mobile: Card layout */
                <div className="divide-y divide-border">
                  {filteredLeads.map((lead) => {
                    const name = getLeadName(lead.lead_profile);
                    const agentStatus = normalizeAgentStatus(lead.agent_status);
                    const style = pipelineStyle[agentStatus] ?? defaultPipelineStyle;
                    return (
                      <Link key={lead.id} to={`/dashboard/leads/${lead.id}`} className="block p-4 hover:bg-muted/30 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm flex-shrink-0">
                            {getInitials(name)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-foreground truncate">{name}</span>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                {lead.status === "CREDITED" && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium border border-amber-500 text-amber-700 bg-amber-50">
                                    Credited
                                  </span>
                                )}
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${style.className}`}>
                                  {style.label}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                              <span>{lead.lead_type_name}</span>
                              <span>·</span>
                              <span>{lead.state}</span>
                              <span>·</span>
                              <span>{formatTimestamp(lead.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                /* Desktop: Table layout */
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">Lead</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">Type</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">State</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">Pipeline</th>
                        <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">Received</th>
                        <th className="text-right px-6 py-4 text-sm font-semibold text-foreground">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredLeads.map((lead) => {
                        const name = getLeadName(lead.lead_profile);
                        const agentStatus = normalizeAgentStatus(lead.agent_status);
                        const style = pipelineStyle[agentStatus] ?? defaultPipelineStyle;
                        return (
                          <tr key={lead.id} className="hover:bg-muted/30 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                                  {getInitials(name)}
                                </div>
                                <span className="font-medium text-foreground">{name}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-muted-foreground">{lead.lead_type_name}</td>
                            <td className="px-6 py-4 text-sm text-muted-foreground">{lead.state}</td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-1.5">
                                {lead.status === "CREDITED" && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium border border-amber-500 text-amber-700 bg-amber-50">
                                    Credited
                                  </span>
                                )}
                                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${style.className}`}>
                                  {style.label}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-muted-foreground">{formatTimestamp(lead.created_at)}</td>
                            <td className="px-6 py-4 text-right">
                              <Button variant="ghost" size="sm" asChild>
                                <Link to={`/dashboard/leads/${lead.id}`}>View</Link>
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {filteredLeads.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <p>No leads match your filters.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Leads;
